<template>
  <div class="packs-wizard-root">
    <Modal class="packs-wizard-modal" icon="car" :title="$t('packs.wizard.title')" @close="onClose">
    <template #text>
      <p class="packs-wizard-modal__subtitle">
        {{ $t('packs.wizard.subtitlePrefix') }}
        <span class="packs-wizard-modal__subtitle-amount">{{ amountLabel }}</span>
        {{ $t('packs.wizard.subtitleSuffix') }}
      </p>

      <!-- Шаг 1: пароль -->
      <div class="packs-step" :class="{ done: step > 1 }">
        <div class="packs-step__number">
          <span :class="{ active: step === 1, done: step > 1 }">
            <Icon v-if="step > 1" name="/base/check.svg" />
            <template v-else>1</template>
          </span>
          <div class="packs-step__line" />
        </div>
        <div class="packs-step__content">
          <div class="packs-step__title">{{ $t('packs.wizard.step1Title') }}</div>
          <template v-if="step === 1">
            <div class="packs-wizard-modal__security">
              <Icon class="packs-wizard-modal__security-icon" name="/packs/security-lock.svg" />
              <span>{{ $t('packs.wizard.securityText') }}</span>
            </div>
            <div class="packs-wizard-modal__inputs">
              <Input
                :value="username"
                icon="/base/user.svg"
                right-icon="/base/copy.svg"
                :disabled="true"
                @rightIconClick="copy(username)"
              />
              <Input
                v-model.trim="password"
                password
                icon="/SignIn/lock.svg"
                :placeholder="$t('packs.wizard.passwordPlaceholder')"
              />
            </div>
            <Button size="lg" :disabled="!isStep1Valid" @click="goToStep2">
              {{ $t('packs.wizard.step1Button') }}
            </Button>
          </template>
        </div>
      </div>

      <!-- Шаг 2: почта -->
      <div class="packs-step" :class="{ done: step > 2 }">
        <div class="packs-step__number">
          <span :class="{ active: step === 2, done: step > 2 }">
            <Icon v-if="step > 2" name="/base/check.svg" />
            <template v-else>2</template>
          </span>
          <div class="packs-step__line" />
        </div>
        <div class="packs-step__content">
          <div class="packs-step__title">{{ $t('packs.wizard.step2Title') }}</div>
          <template v-if="step === 2">
            <p class="packs-wizard-modal__text">
              {{ $t('packs.wizard.step2Text') }}
              <a href="https://www.roblox.com/my/account#!/info" target="_blank" class="packs-wizard-modal__link">
                {{ $t('packs.wizard.step2Link') }}
              </a>
            </p>
            <Button size="lg" @click="goToStep3">
              {{ $t('packs.wizard.step2Button') }}
            </Button>
          </template>
        </div>
      </div>

      <!-- Шаг 3: резервные коды -->
      <div class="packs-step">
        <div class="packs-step__number">
          <span :class="{ active: step === 3 }">3</span>
        </div>
        <div class="packs-step__content">
          <div class="packs-step__title">{{ $t('packs.wizard.step3Title') }}</div>
          <template v-if="step === 3">
            <p class="packs-wizard-modal__text">
              {{ $t('packs.wizard.step3Text') }}
              <a href="https://www.roblox.com/my/account#!/security" target="_blank" class="packs-wizard-modal__link">
                {{ $t('packs.wizard.step3Link') }}
              </a>
            </p>
            <div class="packs-wizard-modal__inputs packs-wizard-modal__inputs--codes">
              <Input
                v-model.trim="backupCode1"
                icon="/packs/shield-key.svg"
                :placeholder="$t('packs.wizard.code1Placeholder')"
                :state="backupCode1Error ? 'error' : 'default'"
                :error="backupCode1Error"
                @blur="backupCode1Touched = true"
              />
              <Input
                v-model.trim="backupCode2"
                icon="/packs/shield-key.svg"
                :placeholder="$t('packs.wizard.code2Placeholder')"
                :state="backupCode2Error ? 'error' : 'default'"
                :error="backupCode2Error"
                @blur="backupCode2Touched = true"
              />
            </div>
            <Button size="lg" :disabled="!isStep3Valid" :loading="loading" @click="onSubmit">
              {{ $t('packs.wizard.step3Button') }}
            </Button>
          </template>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="packs-wizard-modal__footer">
        <Button type="secondary" class="packs-wizard-modal__footer-video" @click="openVideo">
          {{ $t('packs.wizard.videoBtn') }}
        </Button>
        <Button type="secondary" right-icon="/base/support.svg" @click="openChat">
          {{ $t('packs.wizard.helpBtn') }}
        </Button>
      </div>
    </template>
    </Modal>

    <!-- Поверх визарда -->
    <PacksVideoModal v-if="showVideo" @close="showVideo = false" />
  </div>
</template>

<script>
import './packsWizard.scss'

import Modal from '@/components/Base/Modal/modal.vue'
import Input from '@/components/Base/Input/Input.vue'
import Button from '@/components/Base/Button/Button.vue'
import Icon from '@/components/Base/Icon/Icon.vue'
import PacksVideoModal from '@/components/Packs/PacksVideoModal/PacksVideoModal.vue'
import { copyText } from '@/utils/copyText'
import { openChat } from '@/utils/chat'
import { getPackOrderErrorKey, updatePackOrder } from '@/utils/packsApi'

const CODE_LENGTH = 9

export default {
  name: 'PacksWizard',
  components: { Modal, Input, Button, Icon, PacksVideoModal },
  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      step: 1,
      password: '',
      backupCode1: '',
      backupCode2: '',
      backupCode1Touched: false,
      backupCode2Touched: false,
      loading: false,
      showVideo: false
    }
  },
  computed: {
    username() {
      return this.order.nickname || this.order.username || ''
    },
    amountLabel() {
      // на дереве показывается product_name («80 робуксов»); у нас — кол-во R$ пака
      if (this.order.product_name) return this.order.product_name
      return this.$t('packs.wizard.amount', { count: this.order.quantity || 0 })
    },
    isStep1Valid() {
      return !!this.username && !!this.password
    },
    isStep3Valid() {
      return this.backupCode1.length === CODE_LENGTH && this.backupCode2.length === CODE_LENGTH
    },
    backupCode1Error() {
      return this.backupCode1Touched && this.backupCode1.length > 0 && this.backupCode1.length !== CODE_LENGTH
        ? this.$t('packs.wizard.codeError')
        : ''
    },
    backupCode2Error() {
      return this.backupCode2Touched && this.backupCode2.length > 0 && this.backupCode2.length !== CODE_LENGTH
        ? this.$t('packs.wizard.codeError')
        : ''
    }
  },
  methods: {
    openChat,
    onClose() {
      this.$emit('close')
    },
    notifyError(description) {
      this.$store.dispatch('notification/setNotification', {
        title: this.$t('general.warning'),
        description,
        type: 'error'
      })
    },
    copy(text) {
      copyText(text, this.$t('packs.wizard.usernameCopied'))
    },
    openVideo() {
      // Видео-инструкция как на дереве (само видео пока не вставлено — плейсхолдер).
      this.showVideo = true
    },
    goToStep2() {
      if (!this.isStep1Valid) return
      this.$analytics.send('purch_packs_4_modal_password', {})
      this.step = 2
    },
    goToStep3() {
      this.$analytics.send('purch_packs_5_modal_email', {})
      this.step = 3
    },
    async onSubmit() {
      if (!this.isStep3Valid) return
      this.$analytics.send('purch_packs_6_modal_backup', {})
      this.loading = true
      try {
        const res = await updatePackOrder({
          orderId: this.order.order_id || this.order._id,
          username: this.username,
          password: this.password,
          // Коды уходят через запятую, провайдер использует первый.
          secret: `${this.backupCode1},${this.backupCode2}`,
          token: this.$cookies.get('access_token')
        })

        if (!res.status) {
          // 403 — заказ не найден, чужой либо уже в неизменяемом статусе.
          // Остальное приходит кодом в data (напр. [5] — ошибка авторизации).
          this.notifyError(
            res.forbidden ? this.$t('packs.errors.updateForbidden') : this.$t(getPackOrderErrorKey(res))
          )
          return
        }

        // Креды заполнены — воркер сам подхватит заказ и отправит провайдеру.
        this.$emit('done')
      } catch (e) {
        this.notifyError(this.$t('payment.errors.server'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
